

import { useEffect, useState } from "react";
import { formatCurrencyFull } from "@/lib/data";
import { formatDate } from "@/lib/dateFormat";
import { vendorsApi } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import PrintableVendorLedger from "./components/PrintableVendorLedger";

interface Payment {
  id: number;
  amount: number;
  payment_date: string;
  paid_at?: string;
  payment_method?: string;
  transaction_id?: string;
  reference?: string;
  notes?: string;
  event?: {
    id: number;
    name: string;
  };
  event_vendor?: {
    event?: {
      name?: string;
    };
  };
}

interface Assignment {
  id: number;
  event_id: number;
  description: string;
  total_amount: number;
  paid_amount: number;
  balance: number;
  event?: {
    name: string;
    start_date: string;
  } | null;
}

interface PaymentLedgerProps {
  vendorId: number;
  vendorName: string;
  vendorBalance: number;
  onClose: () => void;
}

export default function PaymentLedger({ vendorId, vendorName, vendorBalance, onClose }: PaymentLedgerProps) {
  const { settings } = useSettings();
  const [ledger, setLedger] = useState<{
    assignments: Assignment[];
    payments: Payment[];
    totalBilled: number;
    totalPaid: number;
    totalBalance: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'payments'>('summary');

  useEffect(() => {
    loadLedger();
  }, [vendorId]);

  const loadLedger = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch vendor ledger data
      const response = await vendorsApi.getLedger(vendorId);
      
      if (response.success && response.data) {
        setLedger(response.data);
      } else {
        // Fallback: fetch assignments and payments separately
        const [assignmentsRes, paymentsRes] = await Promise.all([
          vendorsApi.getEvents(vendorId),
          vendorsApi.getPayments(vendorId)
        ]);
        
        // Handle assignments data
        const assignmentsRaw: any[] = assignmentsRes.data?.data || assignmentsRes.data || [];
        const assignments: Assignment[] = Array.isArray(assignmentsRaw) ? assignmentsRaw.map((a: any) => ({
          id: a.id,
          event_id: a.event_id,
          description: a.description,
          total_amount: a.total_amount || a.totalBilled || 0,
          paid_amount: a.paid_amount || a.paid || 0,
          balance: a.balance || (a.total_amount || a.totalBilled || 0) - (a.paid_amount || a.paid || 0),
          event: a.event ? { name: a.event.name, start_date: a.event.start_date } : null,
        })) : [];
        
        // Handle payments data
        const paymentsRaw: any[] = paymentsRes.data?.data || paymentsRes.data || [];
        const payments: Payment[] = Array.isArray(paymentsRaw) ? paymentsRaw : [];
        
        const totalBilled = assignments.reduce((sum, a) => sum + (a.total_amount || 0), 0);
        const totalPaid = assignments.reduce((sum, a) => sum + (a.paid_amount || 0), 0);
        
        setLedger({
          assignments,
          payments,
          totalBilled,
          totalPaid,
          totalBalance: totalBilled - totalPaid
        });
      }
    } catch (err: any) {
      console.error("Failed to load ledger:", err);
      setError(err.message || "Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const getEventName = (payment: Payment) => {
    return payment.event?.name || payment.event_vendor?.event?.name || "Direct Payment";
  };

  const getPaymentDate = (payment: Payment) => {
    return payment.payment_date || payment.paid_at || new Date().toISOString();
  };

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [printLedger, setPrintLedger] = useState<any>(null);

  const downloadPDF = async () => {
    if (!ledger) return;

    setPdfGenerating(true);
    try {
      const assignmentData = (ledger.assignments || []).map((a) => ({
        eventName: a.event?.name || `Event #${a.event_id}`,
        description: a.description || "",
        totalAmount: a.total_amount || 0,
        paidAmount: a.paid_amount || 0,
        balance: a.balance || 0,
      }));

      const paymentData = (ledger.payments || []).map((p) => ({
        date: formatDate(getPaymentDate(p)),
        eventName: getEventName(p),
        amount: p.amount || 0,
        method: p.payment_method?.replace(/_/g, " ") || "-",
        reference: p.transaction_id || p.reference || "-",
      }));

      setPrintLedger({
        vendorName,
        totalBilled: ledger.totalBilled,
        totalPaid: ledger.totalPaid,
        totalBalance: ledger.totalBalance,
        assignments: assignmentData,
        payments: paymentData,
        generatedAt: formatDate(new Date()),
      });

      setTimeout(() => {
        window.print();
        setTimeout(() => setPrintLedger(null), 100);
      }, 100);

      // Use toast to notify user to save as PDF
    } catch (error) {
      console.error("Error generating ledger:", error);
    } finally {
      setPdfGenerating(false);
    }
  };

  // Hook for after-print notification
  useEffect(() => {
    const handleAfterPrint = () => {
      if (printLedger) {
        // Optionally show a toast notification
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printLedger]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={loadLedger}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Accent Strip */}
      <div className="relative">
        <div className="h-1 bg-gradient-to-r from-amber-400 to-amber-600 rounded-t-xl"></div>
        <div className="bg-white border border-stone-200 rounded-b-xl p-4">
          <h3 className="text-lg font-bold text-stone-800 mb-1">{vendorName}</h3>
          <p className="text-xs text-stone-500 uppercase tracking-wide">Vendor Payment Ledger</p>
        </div>
      </div>

      {/* Header Stats - Quotation PDF Theme */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-stone-50 rounded-lg p-3 border-l-4 border-amber-500">
          <div className="text-[10px] text-stone-500 uppercase tracking-wide mb-1">Total Billed</div>
          <div className="text-base font-bold text-stone-800">{formatCurrencyFull(ledger?.totalBilled || 0)}</div>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 border-l-4 border-emerald-500">
          <div className="text-[10px] text-stone-500 uppercase tracking-wide mb-1">Total Paid</div>
          <div className="text-base font-bold text-emerald-700">{formatCurrencyFull(ledger?.totalPaid || 0)}</div>
        </div>
        <div className={`rounded-lg p-3 border-l-4 ${(ledger?.totalBalance || 0) > 0 ? 'bg-amber-50 border-amber-500' : 'bg-emerald-50 border-emerald-500'}`}>
          <div className="text-[10px] text-stone-500 uppercase tracking-wide mb-1">Balance</div>
          <div className={`text-base font-bold ${(ledger?.totalBalance || 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
            {formatCurrencyFull(ledger?.totalBalance || 0)}
          </div>
        </div>
      </div>

      {/* Tabs - Gold Accent Theme */}
      <div className="flex border-b-2 border-stone-200">
        <button
          onClick={() => setActiveTab('summary')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-0.5 transition-all ${
            activeTab === 'summary' 
              ? 'border-amber-500 text-amber-700 bg-amber-50' 
              : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
          }`}
        >
          Assignments
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-0.5 transition-all ${
            activeTab === 'payments' 
              ? 'border-amber-500 text-amber-700 bg-amber-50' 
              : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
          }`}
        >
          Payments ({ledger?.payments?.length || 0})
        </button>
      </div>

      {/* Content - Quotation PDF Theme Tables */}
      {activeTab === 'summary' ? (
        <div className="max-h-[250px] overflow-y-auto border border-stone-200 rounded-lg">
          {!ledger?.assignments || ledger.assignments.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">
              No assignments found
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-amber-50 to-amber-100 sticky top-0 border-b-2 border-amber-200">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Event</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden sm:table-cell">Description</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Total</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Paid</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {ledger.assignments.map((assignment, idx) => (
                  <tr key={assignment.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-stone-800 text-xs">{assignment.event?.name || `Event #${assignment.event_id}`}</div>
                    </td>
                    <td className="px-3 py-2.5 text-stone-500 text-xs hidden sm:table-cell">
                      {assignment.description || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-stone-700 text-xs">
                      {formatCurrencyFull(assignment.total_amount)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 text-xs">
                      {formatCurrencyFull(assignment.paid_amount)}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-bold text-xs ${assignment.balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {formatCurrencyFull(assignment.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="max-h-[250px] overflow-y-auto border border-stone-200 rounded-lg">
          {!ledger?.payments || ledger.payments.length === 0 ? (
            <div className="text-center py-8 text-stone-400 text-sm">
              No payments recorded yet
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-amber-50 to-amber-100 sticky top-0 border-b-2 border-amber-200">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Date</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Event</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Amount</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden sm:table-cell">Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {ledger.payments.map((payment, idx) => (
                  <tr key={payment.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-3 py-2.5 text-stone-600 text-xs">
                      {formatDate(getPaymentDate(payment))}
                    </td>
                    <td className="px-3 py-2.5 text-stone-700 font-medium text-xs">
                      {getEventName(payment)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-emerald-600 text-xs">
                      {formatCurrencyFull(payment.amount)}
                    </td>
                    <td className="px-3 py-2.5 text-stone-500 capitalize text-xs hidden sm:table-cell">
                      {payment.payment_method?.replace('_', ' ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Actions - Gold Theme */}
      <div className="flex gap-3 pt-3 border-t-2 border-amber-200">
        <button
          onClick={downloadPDF}
          disabled={pdfGenerating}
          className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold text-sm hover:from-amber-600 hover:to-amber-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          {pdfGenerating ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF
            </>
          )}
        </button>
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 rounded-lg bg-stone-100 text-stone-700 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Close
        </button>
      </div>

      {printLedger && (
        <PrintableVendorLedger
          vendorName={printLedger.vendorName}
          totalBilled={printLedger.totalBilled}
          totalPaid={printLedger.totalPaid}
          totalBalance={printLedger.totalBalance}
          assignments={printLedger.assignments}
          payments={printLedger.payments}
          generatedAt={printLedger.generatedAt}
          businessInfo={{
            name: settings?.business?.businessName || 'Shalimar',
            logoUrl: settings?.logoUrl,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
          }}
        />
      )}
    </div>
  );
}
